import { Dangerous, ElectricBolt, FastRewind, Settings, WavingHand } from "@mui/icons-material"
import { Box, Dialog, DialogContent, Fab, IconButton, ListItem, Typography, Button } from "@mui/material"
import { QueryClient, useMutation } from "@tanstack/react-query"
import { createComment } from "../../controllers/posts/comments"
import { UserContext } from "../../context/UserContext"
import { useContext, useState } from "react"
import { deleteMatch, editMatch } from "../../controllers/hey_match/match"
import { getOAuthAuthorizeUrl } from "../../controllers/accounts/auth"
import { useNavigate } from "react-router"
import { AccountShow } from "../../views/account/show"
import { SwipeCardSkeleton } from "./SwipeCardSkeleton"
import LocationFilter from "../location.filter"

export const SwipeCards = ({
    setMatches,
    setAccount,
    filterAccounts,
    local,
    setLocal,
    usingLocationFilter,
    miles,
    setMiles,
    setLat,
    setLng,
    accts_viewed,
    count,
    offset,
    setOffset,
    swipeIdx,
    setSwipeIdx,
    account,
    errImg,
    setErrImg
}) => {
    const { userDetails } = useContext(UserContext)
    const [openDialog, setOpenDialog] = useState(false)
    const [acct_count, setAcctCount] = useState(0)
    const navigate = useNavigate()

    const queryClient = new QueryClient()
    const mutation = useMutation({
        mutationFn: createComment,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['createComment'] }),
    })
    const match_mutation = useMutation({
        mutationFn: editMatch,
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['editMatch'] })
            // Keep the parent's viewed list in sync with what was just persisted —
            // otherwise the next swipe builds off a stale array and each save
            // overwrites the server's list with only the most recent id.
            setMatches(prev => ({ ...prev, heymatch: { ...prev?.heymatch, viewed: variables.viewed } }))
        },
    })
    const match_delete_mutation = useMutation({
        mutationFn: deleteMatch,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['editMatch'] })
            setOpenDialog(false)
            setMatches({})
        },
    })

    const resetMatches = () => {
        match_delete_mutation.mutate({ id: userDetails?.id, token: userDetails?.token })
    }

    const send_reply = (status) => {
        mutation.mutate({ body: { status, visibility: "public" }, token: userDetails?.token })
    }

    const udpate_match = (x) => {
        if (x) setAcctCount(acct_count - 1)
        else setAcctCount(acct_count + 1)
        const filteredView = accts_viewed?.filter(item => item !== accts_viewed[accts_viewed.length - 1])
        match_mutation.mutate({
            id: userDetails?.id,
            viewed: x ? filteredView : [...accts_viewed, account.id],
            token: userDetails?.token
        })
    }

    const handleNext = (type) => {
        if (!userDetails?.id) { window.location.assign(getOAuthAuthorizeUrl()); return }
        if (type === 'rewind') {
            setSwipeIdx(swipeIdx - 1)
            setAccount(filterAccounts[swipeIdx - 1])
            udpate_match('remove')
            return
        }
        if (swipeIdx >= count) {
            setSwipeIdx(0)
            setAccount(filterAccounts[0])
            setOffset(offset + 1)
        } else {
            setSwipeIdx(swipeIdx + 1)
            setAccount(filterAccounts[swipeIdx + 1])
        }
        udpate_match(null)
        if (type === 'hey') send_reply("@" + account?.acct + " hey")
        if (type === 'super') send_reply("@" + account?.acct + " SUPER hey!")
    }

    if (!account) return <SwipeCardSkeleton showActions={!!userDetails?.id} />

    return (
        <Box sx={{ width: '100%', maxWidth: 520, mx: 'auto', position: 'relative', pb: 10 }}>

            {/* Settings button top-right */}
            {userDetails?.id && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <IconButton
                        size="small"
                        onClick={() => setOpenDialog(true)}
                        sx={{
                            background: 'rgba(115,41,130,0.09)',
                            color: '#732982',
                            '&:hover': { background: 'rgba(115,41,130,0.18)' }
                        }}
                    >
                        <Settings fontSize="small" />
                    </IconButton>
                </Box>
            )}

            {/* Profile content */}
            <Box sx={{
                borderRadius: 3,
                boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
                background: '#fff',
                overflow: 'visible',
            }}>
                {userDetails?.id
                    ? <AccountShow swipe_id={account.id} />
                    : (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <img src={account?.avatar} alt="" style={{ width: '100%', borderRadius: 12 }} />
                            <Typography sx={{ mt: 1.5, fontWeight: 700 }}>{account?.acct}</Typography>
                            <Typography variant="body2" color="text.secondary">{account?.note}</Typography>
                        </Box>
                    )
                }
            </Box>

            {/* Floating action bar */}
            <Box sx={{
                position: 'fixed',
                bottom: { xs: 70, sm: 24 },
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1.5, sm: 2.5 },
                zIndex: 20,
                background: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(12px)',
                borderRadius: 50,
                px: { xs: 2.5, sm: 3.5 },
                py: 1.5,
                boxShadow: '0 4px 32px rgba(0,0,0,0.14)',
                border: '1px solid rgba(255,255,255,0.8)',
            }}>
                {/* Rewind */}
                {acct_count > 0 && userDetails?.id && (
                    <Fab
                        size="small"
                        onClick={() => handleNext('rewind')}
                        sx={{
                            background: 'linear-gradient(135deg,#FFED00,#FF8C00)',
                            color: '#fff',
                            boxShadow: '0 2px 10px rgba(255,140,0,0.4)',
                            '&:hover': { background: 'linear-gradient(135deg,#FF8C00,#FFED00)' },
                        }}
                    >
                        <FastRewind />
                    </Fab>
                )}

                {/* Skip */}
                <Fab
                    onClick={() => handleNext('skip')}
                    sx={{
                        background: 'linear-gradient(135deg,#E40303,#c40000)',
                        color: '#fff',
                        width: 56, height: 56,
                        boxShadow: '0 4px 16px rgba(228,3,3,0.45)',
                        '&:hover': { background: 'linear-gradient(135deg,#c40000,#E40303)' },
                    }}
                >
                    <Dangerous sx={{ fontSize: 28 }} />
                </Fab>

                {/* Hey — primary action, biggest */}
                <Fab
                    onClick={() => handleNext('hey')}
                    sx={{
                        background: 'linear-gradient(135deg,#008026,#00b33a)',
                        color: '#fff',
                        width: 68, height: 68,
                        boxShadow: '0 4px 20px rgba(0,128,38,0.5)',
                        '&:hover': { background: 'linear-gradient(135deg,#00b33a,#008026)' },
                    }}
                >
                    <WavingHand sx={{ fontSize: 32 }} />
                </Fab>

                {/* Super hey */}
                <Fab
                    onClick={() => handleNext('super')}
                    sx={{
                        background: 'linear-gradient(135deg,#24408E,#732982)',
                        color: '#fff',
                        width: 56, height: 56,
                        boxShadow: '0 4px 16px rgba(36,64,142,0.45)',
                        '&:hover': { background: 'linear-gradient(135deg,#732982,#24408E)' },
                    }}
                >
                    <ElectricBolt sx={{ fontSize: 26 }} />
                </Fab>
            </Box>

            {/* Settings dialog */}
            {userDetails?.id && (
                <Dialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    PaperProps={{ sx: { borderRadius: 3, m: 2 } }}
                    // The Google Places dropdown for the Distance Filter below is
                    // appended outside this dialog (to document.body), so MUI's
                    // default focus trap swallows clicks/keyboard selection on it —
                    // without this, picking a suggestion never fires, Enter submits
                    // with no place selected, and onPlaceSelected crashes on
                    // `place.geometry` because `place` comes back undefined.
                    disableEnforceFocus
                    disableAutoFocus
                >
                    <Box sx={{ height: 4, background: 'linear-gradient(90deg,#E40303,#FF8C00,#FFED00,#008026,#24408E,#732982)' }} />
                    <DialogContent>
                        <Typography fontWeight={800} fontSize={16} sx={{ mb: 2 }}>Settings</Typography>
                        <ListItem sx={{ display: 'block', textAlign: 'left', px: 0 }}>
                            <Typography fontWeight={700} fontSize={14} sx={{ mb: 1 }}>Distance Filter</Typography>
                            <LocationFilter miles={miles} setMiles={setMiles} setLat={setLat} setLng={setLng} />
                            {usingLocationFilter && (
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    color="error"
                                    onClick={() => {
                                        setLat('')
                                        setLng('')
                                        const addressInput = document.getElementById('google_location')
                                        if (addressInput) addressInput.value = ''
                                    }}
                                    sx={{ borderRadius: 50, textTransform: 'none', mt: 1 }}
                                >
                                    Clear Distance Filter
                                </Button>
                            )}
                        </ListItem>
                        <ListItem sx={{ display: 'block', textAlign: 'center', px: 0 }}>
                            {usingLocationFilter ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    A distance filter is active, so you're only viewing MakeGayFriends.com
                                    members (the only members we have location data for). Clear the distance
                                    filter above to search connected Mastodon servers again.
                                </Typography>
                            ) : (
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        {local
                                            ? "Currently showing MakeGayFriends members only."
                                            : "Currently showing members from all connected Mastodon servers."}
                                    </Typography>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() => { setLocal(!local); setOpenDialog(false) }}
                                        sx={{ borderRadius: 50, textTransform: 'none', mb: 1 }}
                                    >
                                        {local ? "Show all servers" : "Show MakeGayFriends only"}
                                    </Button>
                                </>
                            )}
                        </ListItem>
                        <ListItem sx={{ display: 'block', textAlign: 'center', px: 0 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                Reset your view history to start seeing profiles from the beginning.
                            </Typography>
                            <Button
                                fullWidth
                                variant="outlined"
                                color="error"
                                onClick={resetMatches}
                                sx={{ borderRadius: 50, textTransform: 'none' }}
                            >
                                Reset history
                            </Button>
                        </ListItem>
                    </DialogContent>
                </Dialog>
            )}
        </Box>
    )
}
